import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { userQueryKeys } from "@/hooks/userQueryKeys";
import { usersApi } from "@/lib/humhub-api/users";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";
import { updateMembershipBlockStatus } from "@/lib/updateMembershipBlockStatus";
import type { UseBlockUserOptions } from "./useBlockUser";

export const useUnblockUser = ({
  invalidateUserInfo = false,
}: UseBlockUserOptions = {}) => {
  const queryClient = useQueryClient();

  const unblockUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      return usersApi.unblockUser(userId);
    },
    onMutate: async (userId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: userQueryKeys.blocked() });

      // Snapshot the previous value
      const previousBlocked = queryClient.getQueryData(userQueryKeys.blocked());

      // Optimistically update to the new value
      queryClient.setQueryData<string[]>(userQueryKeys.blocked(), (old) => {
        if (Array.isArray(old)) {
          return old.filter((id: string) => id !== String(userId));
        }
        return old;
      });

      // Return a context object with the snapshotted value
      return { previousBlocked };
    },
    onError: (err, _userId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBlocked) {
        queryClient.setQueryData(
          userQueryKeys.blocked(),
          context.previousBlocked,
        );
      }
      console.error("useUnblockUser: Error unblocking user:", err);
    },
    onSuccess: (_success, userId) => {
      if (typeof userId === "number") {
        // UIを即時更新するためにキャッシュを先に書き換える
        updateMembershipBlockStatus(queryClient, userId, false);
        queryInvalidationHelpers.invalidateOnUserBlockChange(queryClient, {
          userId,
          invalidateUserInfo,
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: userQueryKeys.blocked() });
    },
  });

  const unblockUser = useCallback(
    async (userId: number) => {
      return unblockUserMutation.mutateAsync(userId);
    },
    [unblockUserMutation],
  );

  return {
    unblockUser,
    isUnblocking: unblockUserMutation.isPending,
    unblockError: unblockUserMutation.error,
    unblockSuccess: unblockUserMutation.isSuccess,
  };
};
